import { useCallback, useEffect, useMemo, useState } from 'react';

import { setSessionExpiredHandler } from '@/api/client';
import { adminApi, authApi } from '@/api';
import { tokenStorage } from '@/services/tokenStorage';

import { AuthContext } from './auth-context';

/**
 * Owns session state for the whole application.
 *
 * On boot it restores the session from the stored tokens by calling
 * `GET /auth/me`, so a refresh keeps the user signed in and a revoked session
 * is detected immediately.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('initialising');
  const [pendingVerificationCode, setPendingVerificationCode] = useState(null);
  const [impersonation, setImpersonation] = useState(() =>
    tokenStorage.isImpersonating() ? { restoring: true } : null,
  );

  const signOut = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } catch {
      // Signing out locally must succeed even if the request fails.
    } finally {
      tokenStorage.clear();
      setUser(null);
      setStatus('anonymous');
      setPendingVerificationCode(null);
    }
  }, []);

  const applySession = useCallback((session) => {
    tokenStorage.set(session.tokens);
    setUser(session.user);
    setStatus('authenticated');
    setPendingVerificationCode(session.verification_code_debug ?? null);
    return session;
  }, []);

  const login = useCallback(
    async (credentials) => applySession(await authApi.login(credentials)),
    [applySession],
  );

  const registerStudent = useCallback(
    async (input) => applySession(await authApi.registerStudent(input)),
    [applySession],
  );

  const registerCompany = useCallback(
    async (input) => applySession(await authApi.registerCompany(input)),
    [applySession],
  );

  const refreshUser = useCallback(async () => {
    const current = await authApi.getCurrentUser();
    setUser(current);
    setStatus('authenticated');
    if (current.is_email_verified) setPendingVerificationCode(null);
    return current;
  }, []);

  /**
   * Sign in as another user, keeping the admin's own session to come back to.
   *
   * The server issues an access token and no refresh token, so this session
   * expires on its own -- there is nothing to revoke and no "stop" call. The
   * banner and the exit button are the whole of the client's side of it.
   */
  const impersonate = useCallback(async (userId, reason) => {
    // `reason` is optional; the server audits the act with or without one.
    const session = await adminApi.impersonate(userId, reason);
    tokenStorage.startImpersonation(session.access_token);
    setUser(session.user);
    setStatus('authenticated');
    setImpersonation({
      user: session.user,
      impersonatorName: session.impersonator_name,
      expiresAt: Date.now() + session.expires_in * 1000,
    });
    return session;
  }, []);

  const stopImpersonating = useCallback(async () => {
    const restored = tokenStorage.stopImpersonation();
    setImpersonation(null);
    if (!restored) {
      // Nothing to go back to -- treat it as a sign-out rather than leaving
      // them holding a token for an account they are not.
      tokenStorage.clear();
      setUser(null);
      setStatus('anonymous');
      return;
    }
    const current = await authApi.getCurrentUser();
    setUser(current);
    setStatus('authenticated');
  }, []);

  // Restore the session on boot.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!tokenStorage.hasSession()) {
        if (!cancelled) setStatus('anonymous');
        return;
      }
      try {
        const current = await authApi.getCurrentUser();
        if (!cancelled) {
          setUser(current);
          setStatus('authenticated');
          // The token outlives a page refresh, so the banner has to as well --
          // an admin who reloads must not silently keep acting as somebody
          // else with nothing on screen to say so.
          setImpersonation(tokenStorage.isImpersonating() ? { user: current } : null);
        }
      } catch {
        if (!cancelled) {
          // An expired impersonation token must drop the admin back into their
          // own session rather than signing them out of everything.
          if (tokenStorage.stopImpersonation()) {
            setImpersonation(null);
            try {
              const admin = await authApi.getCurrentUser();
              if (!cancelled) {
                setUser(admin);
                setStatus('authenticated');
              }
              return;
            } catch {
              /* fall through to signing out */
            }
          }
          tokenStorage.clear();
          setUser(null);
          setStatus('anonymous');
        }
      }
    }

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // A failed refresh in the API client must clear the UI session too.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      // An impersonation token carries no refresh token, so it always ends by
      // arriving here. Expiring out of somebody else's account should put the
      // admin back in their own, not sign them out of everything -- their own
      // session was never invalidated and is sitting right there.
      if (tokenStorage.stopImpersonation()) {
        setImpersonation(null);
        void stopImpersonatingRestore();
        return;
      }
      setUser(null);
      setStatus('anonymous');
    });
    return () => setSessionExpiredHandler(() => {});

    async function stopImpersonatingRestore() {
      try {
        const admin = await authApi.getCurrentUser();
        setUser(admin);
        setStatus('authenticated');
      } catch {
        tokenStorage.clear();
        setUser(null);
        setStatus('anonymous');
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated' && user !== null,
      login,
      registerStudent,
      registerCompany,
      refreshUser,
      setUser,
      signOut,
      pendingVerificationCode,
      impersonate,
      stopImpersonating,
      impersonation,
      isImpersonating: impersonation !== null,
    }),
    [
      user,
      status,
      login,
      registerStudent,
      registerCompany,
      refreshUser,
      signOut,
      pendingVerificationCode,
      impersonate,
      stopImpersonating,
      impersonation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
